import { CurrencyPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { DialogWrapperComponent } from '../../wrappers/dialog-wrapper/dialog-wrapper.component';
import { ButtonComponent } from '../../button/button.component';
import {
  FormBuilder,
  FormControl,
  FormRecord,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { WooCommerceProduct } from '../../../../core/interfaces/woo-commerce/api/products/product-api.interface';
import { CounterComponent } from '../../controllers/counter/counter.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { finalize, startWith } from 'rxjs';
import { WooCommerceCartItem } from '../../../../core/interfaces/woo-commerce/api/cart/cart.interface';
import {
  HappyAddonPayloadEntry,
  WooCommercePostCartItemBody
} from '../../../../core/interfaces/woo-commerce/cart/cart.interface';
import {
  WooCommerceProductAddonDisplay,
  WooCommerceProductAddonType
} from '../../../../core/enums/woo-commerce/api/products/product-api.enum';
import { InputComponent } from '../../controllers/input/input.component';
import { TextareaComponent } from '../../controllers/textarea/textarea.component';
import {
  DropdownComponent,
  DropdownSize,
  DropdownStyle
} from '../../controllers/dropdown/dropdown.component';
import { RadioComponent } from '../../controllers/radio/radio.component';
import { CheckboxListComponent } from '../../controllers/checkbox-list/checkbox-list.component';
import { FormattedCurrencyPipe } from '../../../pipes/formatted-currency.pipe';
import {
  WooCommerceBaseProductAddon,
  WooCommerceProductAddonConditionalRule,
  WooCommerceProductAddonOption
} from '../../../../core/interfaces/woo-commerce/api/products/base-product-api.interface';
import { WooCommercePopularProductsItem } from '../../../../core/interfaces/woo-commerce/api/products/popular-product-api.interface';
import { CustomArrayValidators } from '../../../validators/array.validators';
import { Store } from '@ngxs/store';
import { CartItemsAction } from '../../../state/cart/cart.actions';
import { ButtonStyle } from '../../../enums/button.enum';
import { replacePreTags } from '../../../utils/text.utils';
import {
  GA4Item,
  GoogleAnalyticsService
} from '../../../../core/services/google-analytics.service';
import { CartState } from '../../../state/cart/cart.state';
import { ProductImagePipe } from '../../../pipes/product-image.pipe';
import { removeEmptyFields } from '../../../utils/remove-empty-fields.util';
import { HappyProductVariant } from '../../../../core/interfaces/happy-api/happy-api.interface';

interface Prices {
  price: number;
  regular_price: number;
  currency_minor_unit?: number;
}

type AddonsConfiguration = Record<string, string | string[] | number | number[]>;
type RawHappyAddonFieldOption = {
  id?: number | string;
  name?: string;
  amount?: number | string;
  is_default?: boolean;
};
type RawHappyAddonField = {
  id?: number | string;
  type?: string;
  title?: string;
  description?: string | null;
  required?: boolean;
  max_options?: number;
  display_type?: string | null;
  options?: RawHappyAddonFieldOption[];
};

@UntilDestroy()
@Component({
  selector: 'app-meal-selection-dialog',
  imports: [
    DialogWrapperComponent,
    ButtonComponent,
    FormsModule,
    CurrencyPipe,
    CounterComponent,
    ReactiveFormsModule,
    InputComponent,
    TextareaComponent,
    DropdownComponent,
    RadioComponent,
    CheckboxListComponent,
    FormattedCurrencyPipe,
    DecimalPipe,
    ProductImagePipe,
    NgTemplateOutlet
  ],
  templateUrl: './meal-selection-dialog.component.html',
  styleUrl: './meal-selection-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MealSelectionDialogComponent implements OnInit {
  product: WooCommerceProduct | WooCommercePopularProductsItem =
    inject(DIALOG_DATA);
  form!: FormRecord;

  totalPrice: WritableSignal<number> = signal<number>(0);
  isAddToCartLoading: WritableSignal<boolean> = signal<boolean>(false);
  selectedVariant: WritableSignal<HappyProductVariant | null> = signal<HappyProductVariant | null>(null);
  variantSelectionInvalid: WritableSignal<boolean> = signal<boolean>(false);

  get productVariants(): HappyProductVariant[] {
    return (this.product as WooCommerceProduct)?.variants || [];
  }

  get hasVariants(): boolean {
    return !!(this.product as WooCommerceProduct)?.has_variants && this.productVariants.length > 0;
  }

  selectVariant(variant: HappyProductVariant): void {
    this.selectedVariant.set(variant);
    this.variantSelectionInvalid.set(false);
    const prices = this.getVariantPrices(variant);
    this.productPrices.set(prices);
    this.totalPrice.set(this.calculateTotalPrice(this.addOnsForm?.value || {}));
  }

  getVariantLabel(variant: HappyProductVariant): string {
    if (variant.attribute_values?.length > 0) {
      return variant.attribute_values.map((av) => av.name).join(' / ');
    }
    const parentName = this.product.name.replace(/<[^>]*>/g, '');
    return variant.name.replace(parentName, '').trim() || variant.name;
  }

  processedDescription: Signal<string> = computed(() => {
    const variant = this.selectedVariant();
    const description =
      variant?.description ||
      variant?.short_description ||
      this.product?.description ||
      '';
    return replacePreTags(description);
  });

  productPrices: WritableSignal<Prices> = signal<Prices>(
    this.getPrices(this.product)
  );
  discountPercentage: Signal<number> = computed<number>(() => {
    const prices = this.productPrices();

    if (prices.price && prices.regular_price) {
      const salePrice: number = prices.price;
      const regularPrice: number = prices.regular_price;

      if (!isNaN(salePrice) && !isNaN(regularPrice) && regularPrice > 0) {
        return ((regularPrice - salePrice) / regularPrice) * 100;
      }
    }

    return 0;
  });

  get addOnsForm(): FormRecord {
    return this.form.get('addons_configuration') as FormRecord;
  }

  /**
   * Returns root addons (addons without conditional_rules or with empty conditional_rules)
   */
  get rootAddons(): WooCommerceBaseProductAddon[] {
    return (
      this.productAddons.filter(
        (addon) =>
          !addon.conditional_rules || addon.conditional_rules.length === 0
      ) || []
    );
  }

  /**
   * Returns sub-addons that should be shown when a specific option is selected in a parent addon.
   * @param parentSlug - The slug of the parent addon
   * @param optionIndex - The 0-based index of the selected option (will be converted to 1-based for comparison)
   */
  getSubAddonsForOption(
    parentSlug: string,
    optionIndex: number
  ): WooCommerceBaseProductAddon[] {
    const targetValue = optionIndex + 1; // conditional_rules.value is 1-based

    return (
      this.productAddons.filter((addon) => {
        if (!addon.conditional_rules || addon.conditional_rules.length === 0) {
          return false;
        }

        return addon.conditional_rules.some(
          (rule: WooCommerceProductAddonConditionalRule) =>
            rule.field === parentSlug && rule.value === targetValue
        );
      }) || []
    );
  }

  /**
   * Checks if a sub-addon should be visible based on current form selection
   * @param subAddon - The sub-addon to check visibility for
   */
  isSubAddonVisible(subAddon: WooCommerceBaseProductAddon): boolean {
    if (
      !subAddon.conditional_rules ||
      subAddon.conditional_rules.length === 0
    ) {
      return true; // No conditions means always visible
    }

    return subAddon.conditional_rules.some(
      (rule: WooCommerceProductAddonConditionalRule) => {
        // Find the parent addon by slug
        const parentAddon = this.productAddons.find(
          (addon) => addon.slug === rule.field
        );

        if (!parentAddon) {
          return false;
        }

        const parentFormValue = this.addOnsForm.get(parentAddon.key)?.value;

        if (!parentFormValue) {
          return false;
        }

        // Get the option index that is currently selected
        const selectedOptionIndex = parentAddon.options.findIndex((opt) => {
          if (Array.isArray(parentFormValue)) {
            return parentFormValue.includes(opt.value);
          }

          return parentFormValue === opt.value;
        });

        // rule.value is 1-based, so we compare with selectedOptionIndex + 1
        return (
          selectedOptionIndex !== -1 && selectedOptionIndex + 1 === rule.value
        );
      }
    );
  }

  /**
   * Checks if a specific option is currently selected in a parent addon
   * @param parentAddon - The parent addon
   * @param optionIndex - The 0-based index of the option
   */
  isOptionSelected(
    parentAddon: WooCommerceBaseProductAddon,
    optionIndex: number
  ): boolean {
    const parentFormValue = this.addOnsForm.get(parentAddon.key)?.value;

    if (parentFormValue == null || parentFormValue === '') {
      return false;
    }

    const option = parentAddon.options[optionIndex];
    if (!option) {
      return false;
    }

    if (Array.isArray(parentFormValue)) {
      return parentFormValue.includes(option.value);
    }

    return parentFormValue === option.value;
  }

  protected readonly WooCommerceProductAddonType = WooCommerceProductAddonType;
  protected readonly WooCommerceProductAddonDisplay =
    WooCommerceProductAddonDisplay;
  protected readonly DropdownStyle = DropdownStyle;
  protected readonly DropdownSize = DropdownSize;
  protected readonly ButtonStyle = ButtonStyle;

  constructor(
    private dialogRef: DialogRef<WooCommerceProduct>,
    private toastrService: ToastrService,
    private formBuilder: FormBuilder,
    private store: Store,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {}

  ngOnInit() {
    this.initForm(this.product.id, this.productAddons);
    this.autoSelectFirstVariant();
    this.subscribeOnAddonsChanges();

    // Track view_item event in Google Analytics
    this.trackViewItem();
  }

  private autoSelectFirstVariant(): void {
    if (!this.hasVariants) {
      return;
    }
    const first =
      this.productVariants.find((v) => v.is_in_stock) ??
      this.productVariants[0];
    if (first) {
      this.selectVariant(first);
    }
  }

  private trackViewItem(): void {
    try {
      const prices = this.productPrices();
      const totals = this.store.selectSnapshot(CartState.totals);
      const currencyDivider = 10 ** (prices.currency_minor_unit || 0);
      const price = prices.price / (currencyDivider || 1);

      // Get category from product if available
      const categories = (this.product as WooCommerceProduct)?.categories || [];
      const primaryCategory =
        categories.length > 0 ? categories[0]?.name : undefined;
      const secondaryCategory =
        categories.length > 1 ? categories[1]?.name : undefined;

      const ga4Item: GA4Item = {
        item_id: `${this.product.id}`,
        item_name: this.product.name.replace(/<[^>]*>/g, ''), // Strip HTML tags
        price: price,
        quantity: 1,
        item_category: primaryCategory,
        item_category2: secondaryCategory
      };

      this.googleAnalyticsService.trackViewItem({
        currency: totals?.currency_code || 'USD',
        value: price,
        items: [ga4Item]
      });
    } catch (error) {
      console.error('Failed to track view item:', error);
    }
  }

  onAddToCartClick() {
    if (this.hasVariants && !this.selectedVariant()) {
      this.variantSelectionInvalid.set(true);
      this.toastrService.error('Please select a size or variant!');
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastrService.error('Please fill all required fields!');
      return;
    }

    this.isAddToCartLoading.set(true);

    const addonsConfiguration = removeEmptyFields(
      (this.form.value['addons_configuration'] || {}) as AddonsConfiguration
    );
    const variant = this.selectedVariant();
    const formValues: WooCommercePostCartItemBody = {
      id: this.form.value['id'],
      quantity: this.form.value['quantity'],
      addons: this.mapAddonsPayload(addonsConfiguration),
      ...(variant != null ? { variant_id: variant.id } : {})
    };

    const displayData = this.constructDisplayData(addonsConfiguration);

    this.store
      .dispatch(new CartItemsAction.Add(formValues, displayData))
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isAddToCartLoading.set(false))
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            'The product has been added to cart!',
            'Added to cart!'
          );

          this.dialogRef.close();
        }
      });
  }

  private constructDisplayData(
    addonsConfig: AddonsConfiguration
  ): Partial<WooCommerceCartItem> {
    const prices = this.productPrices();
    const itemData: any[] = [];

    if (this.productAddons.length > 0) {
      this.productAddons.forEach((addon) => {
        const selectedValue = addonsConfig[addon.key];

        if (selectedValue != null && selectedValue !== '') {
          const values = Array.isArray(selectedValue)
            ? selectedValue
            : [selectedValue];

          values.forEach((val) => {
            const option = addon.options.find(
              (opt) => opt.value.toString() === val.toString()
            );
            if (option) {
              // Construct display string similar to backend
              // E.g. "Option Label ($1.00)"
              let display = option.label;
              if (option.price && parseFloat(option.price) > 0) {
                // Formatting is approximate
                display += ` (+ $${option.price})`;
              }

              itemData.push({
                name: addon.name,
                display: display,
                value: option.label,
                price: option.price
              });
            }
          });
        }
      });
    }

    const totalAddons = itemData.reduce(
      (acc, cur) => acc + cur.price * 10 ** (prices.currency_minor_unit || 2),
      0
    );

    const variant = this.selectedVariant();
    const variantLabel = variant ? this.getVariantLabel(variant) : null;
    const displayName = variant ? variant.name : this.product.name;
    const displayImages = this.resolveDisplayImages(variant);

    if (variantLabel) {
      itemData.unshift({ name: 'Size', display: variantLabel, value: variantLabel, price: 0 });
    }

    return {
      name: displayName,
      clover_pos_images: this.product.clover_pos_images,
      prices: {
        price: (prices.price + totalAddons).toString(),
        regular_price: (prices.regular_price + totalAddons).toString(),
        sale_price: (prices.price + totalAddons).toString(),
        price_range: null,
        raw_prices: {
          precision: prices.currency_minor_unit || 2,
          price: prices.price.toString(),
          regular_price: prices.regular_price.toString(),
          sale_price: prices.price.toString()
        },
        currency_minor_unit: prices.currency_minor_unit || 2,
        currency_code: 'USD',
        currency_symbol: '$',
        currency_decimal_separator: '.',
        currency_thousand_separator: ',',
        currency_prefix: '$',
        currency_suffix: ''
      },
      images: displayImages,
      item_data: itemData,
      quantity_limits: {
        minimum: 1,
        maximum: 100,
        multiple_of: 1,
        editable: true
      },
      variation: []
    };
  }

  private resolveDisplayImages(variant: HappyProductVariant | null): any[] {
    if (variant?.main_image?.url) {
      return [{
        src: variant.main_image.url,
        name: variant.main_image.name || '',
        alt: variant.name,
        sizes: {}
      }];
    }
    return (this.product.images as any) || [];
  }

  private getVariantPrices(variant: HappyProductVariant): Prices {
    const minorUnit = (this.product as WooCommerceProduct)?.prices?.currency_minor_unit ?? 2;
    const factor = 10 ** minorUnit;
    return {
      price: Math.round((variant.effective_price ?? variant.regular_price ?? 0) * factor),
      regular_price: Math.round((variant.regular_price ?? 0) * factor),
      currency_minor_unit: minorUnit
    };
  }

  private mapAddonsPayload(
    addonsConfig: AddonsConfiguration
  ): Record<string, HappyAddonPayloadEntry> {
    const addonsPayload: Record<string, HappyAddonPayloadEntry> = {};
    const productAddons = this.productAddons;

    productAddons.forEach((addon) => {
      const selectedValue = addonsConfig[addon.key];

      if (
        selectedValue == null ||
        selectedValue === '' ||
        (Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        return;
      }

      const selectedValues = Array.isArray(selectedValue)
        ? selectedValue.map((value) => String(value))
        : [String(selectedValue)];
      const selectedOptions = selectedValues
        .map((value) =>
          addon.options.find((option) => String(option.value) === value)
        )
        .filter((option): option is WooCommerceProductAddonOption => !!option);

      if (selectedOptions.length === 0) {
        return;
      }

      const optionIds = selectedOptions
        .map((option) => option.field_value)
        .filter((value) => value != null && value !== '');

      if (optionIds.length === 0) {
        return;
      }

      const addonFieldId = Number(addon.id);
      if (!Number.isFinite(addonFieldId) || addonFieldId <= 0) {
        return;
      }

      const addonKey = `addon-${addon.field_name}`;
      addonsPayload[addonKey] = {
        addon_field_id: addonFieldId,
        option_ids: optionIds
      };
    });

    return addonsPayload;
  }

  private subscribeOnAddonsChanges() {
    this.addOnsForm.valueChanges
      .pipe(startWith(this.addOnsForm.value), untilDestroyed(this))
      .subscribe({
        next: (formValues) => {
          const totalPrice: number = this.calculateTotalPrice(formValues);
          this.totalPrice.set(totalPrice);
          this.updateSubAddonValidators();
        }
      });
  }

  /**
   * Updates validators for sub-addons based on their visibility.
   * Sub-addons should only be required when their parent option is selected.
   */
  private updateSubAddonValidators() {
    const subAddons =
      this.productAddons.filter(
        (addon) => addon.conditional_rules && addon.conditional_rules.length > 0
      ) || [];

    subAddons.forEach((subAddon) => {
      const formControl = this.addOnsForm.get(subAddon.key);

      if (!formControl) {
        return;
      }

      const isVisible = this.isSubAddonVisible(subAddon);
      const validators = [];

      if (isVisible && subAddon.required) {
        validators.push(Validators.required);
      }

      if (
        isVisible &&
        subAddon.max_options != null &&
        (subAddon as any).max_options !== '' &&
        subAddon.max_options !== 0
      ) {
        validators.push(CustomArrayValidators.maxLength(subAddon.max_options));
      }

      if (
        isVisible &&
        subAddon.min != null &&
        (subAddon as any).min !== '' &&
        subAddon.min !== 0
      ) {
        validators.push(CustomArrayValidators.minLength(subAddon.min));
      }

      formControl.setValidators(validators);
      formControl.updateValueAndValidity({ emitEvent: false });

      // Clear value if sub-addon becomes hidden
      if (!isVisible && formControl.value) {
        const initialValue = this.getInitialFormValue(subAddon);
        formControl.setValue(initialValue, { emitEvent: false });
      }
    });
  }

  private calculateTotalPrice(
    formValues: Record<string, number | number[]> = this.form.value
  ) {
    const currentPrices = this.productPrices();
    const initialPrice: number = currentPrices.price;
    const minorUnit: number = currentPrices.currency_minor_unit || 0;

    const findAddonOptionById = (
      rawFieldValue: number | number[],
      productAddonOptions: WooCommerceProductAddonOption[]
    ): WooCommerceProductAddonOption[] => {
      const fieldValue: number[] = Array.isArray(rawFieldValue)
        ? rawFieldValue
        : [rawFieldValue];

      return productAddonOptions.filter((productAddonOption) => {
        return fieldValue.includes(productAddonOption.value);
      });
    };

    return Object.entries(formValues).reduce(
      (acc, [fieldName, fieldValues]) => {
        const addon = this.product.product_addons?.find((productAddon) => {
          return productAddon.key === fieldName;
        });
        const fallbackAddon = this.productAddons.find((productAddon) => {
          return productAddon.key === fieldName;
        });

        const resolvedAddon = addon || fallbackAddon;
        if (!resolvedAddon) {
          return acc;
        }

        const selectedOptions: WooCommerceProductAddonOption[] =
          findAddonOptionById(fieldValues, resolvedAddon.options);

        if (selectedOptions.length === 0) {
          return acc;
        }

        const total: number = selectedOptions.reduce((acc, selectedOption) => {
          const parsedPrice: number =
            parseFloat(selectedOption.price) * 10 ** minorUnit;
          if (isNaN(parsedPrice)) {
            return acc;
          }

          return acc + parsedPrice;
        }, 0);

        return acc + total;
      },
      initialPrice
    );
  }

  private get productAddons(): WooCommerceBaseProductAddon[] {
    const directAddons = (this.product as WooCommerceProduct)?.product_addons;
    if (Array.isArray(directAddons) && directAddons.length > 0) {
      return directAddons;
    }

    const rawAddonFields =
      ((this.product as any)?.addon_fields as RawHappyAddonField[] | null) || [];
    if (!Array.isArray(rawAddonFields) || rawAddonFields.length === 0) {
      return [];
    }

    const seenSignatures = new Set<string>();
    const normalized: WooCommerceBaseProductAddon[] = [];

    rawAddonFields.forEach((field, fieldIndex) => {
      const addonId = field?.id ?? fieldIndex;
      const fieldName = String(addonId);
      const options = Array.isArray(field?.options) ? field.options : [];

      const signature = `${field?.title || ''}|${field?.type || ''}|${options
        .map((option) => `${option?.name || ''}:${option?.amount ?? 0}`)
        .join('|')}`;
      if (seenSignatures.has(signature)) {
        return;
      }
      seenSignatures.add(signature);

      const normalizedOptions: WooCommerceProductAddonOption[] = options.map(
        (option, optionIndex) => ({
          sku: '',
          label: option?.name || '',
          price: String(option?.amount ?? 0),
          image: '',
          price_type: 'flat_rate',
          checked: option?.is_default ? String(optionIndex) : undefined,
          checked2: '',
          option_checked: [],
          allergens: '',
          ingredients: '',
          ingamounts: '',
          ingramounts: '',
          ingshows: '',
          ingredients_desc: '',
          field_value: String(option?.id ?? option?.name ?? optionIndex + 1),
          value: optionIndex
        })
      );

      normalized.push({
        id: addonId,
        key: fieldName,
        field_name: fieldName,
        slug: fieldName,
        name: field?.title || `Addon ${fieldName}`,
        title_format: 'label',
        description_enable: 0,
        description: field?.description || '',
        type: field?.type || WooCommerceProductAddonType.MultipleChoice,
        display:
          field?.display_type || WooCommerceProductAddonDisplay.RadioButton,
        position: fieldIndex,
        required: field?.required ? 1 : 0,
        front_hide: 0,
        max_options: Number(field?.max_options ?? 1),
        restrictions: 0,
        hide_name: '',
        restrictions_type: '',
        adjust_price: 0,
        price_type: 'flat_rate',
        price: '0',
        min: 0,
        max: 0,
        options: normalizedOptions
      });
    });

    return normalized;
  }

  private initForm(
    productId: number,
    productAddons: WooCommerceBaseProductAddon[] = []
  ) {
    this.form = this.formBuilder.record({
      id: [productId, Validators.required],
      quantity: [1, Validators.required],
      addons_configuration: this.formBuilder.record({})
    });

    productAddons?.forEach((addon: WooCommerceBaseProductAddon) => {
      const initialValue: string | string[] = this.getInitialFormValue(addon);
      const validators = [];

      if (addon.required) {
        validators.push(Validators.required);
      }

      if (
        addon.max_options != null &&
        (addon as any).max_options !== '' &&
        addon.max_options !== 0
      ) {
        validators.push(CustomArrayValidators.maxLength(addon.max_options));
      }

      if (addon.min != null && (addon as any).min !== '' && addon.min !== 0) {
        validators.push(CustomArrayValidators.minLength(addon.min));
      }

      const formControl: FormControl = this.formBuilder.control(
        initialValue,
        validators
      );

      this.addOnsForm.addControl(addon.key, formControl);
    });
  }

  private getInitialFormValue(
    addon: WooCommerceBaseProductAddon
  ): string | string[] {
    const defaultValue: string | undefined = addon.options[0]?.checked;
    const isArrayValue: boolean =
      addon.type === WooCommerceProductAddonType.Checkbox ||
      (addon.type === WooCommerceProductAddonType.MultipleChoice &&
        addon.display === WooCommerceProductAddonDisplay.RadioButton);

    if (defaultValue == null) {
      return isArrayValue ? [] : '';
    }

    return isArrayValue ? [defaultValue] : defaultValue;
  }

  private getPrices(
    product: WooCommerceProduct | WooCommercePopularProductsItem = this.product
  ): Prices {
    if ((product as WooCommerceProduct)['prices'] != null) {
      const prices = (product as WooCommerceProduct)['prices'];
      return {
        price: parseFloat(prices.price) || 0,
        regular_price: parseFloat(prices.regular_price) || 0,
        currency_minor_unit: prices.currency_minor_unit
      };
    } else {
      const prices = product as WooCommercePopularProductsItem;
      return {
        price: parseFloat(prices.price) || 0,
        regular_price: parseFloat(prices.regular_price) || 0,
        currency_minor_unit: 0
      };
    }
  }
}
